import React from "react";
import ReactDOM from "react-dom/client";
import axios from "axios";
import { toast } from "sonner";
import { publishMessage } from "./lib/messageBus";
import { avviaControlloVersione } from "./lib/versionCheck";
import "./index.css";
import App from "./App";

// Monkey-patch sonner: ogni toast pubblica anche sul bus globale, così il
// MessageBanner accanto al nome dell'app può mostrare l'ultima notifica
// in modo persistente fino al prossimo evento.
(function patchToast() {
  const _types = ['success', 'error', 'info', 'warning', 'loading', 'message'];
  for (const t of _types) {
    const original = toast[t];
    if (typeof original !== 'function') continue;
    toast[t] = function patched(msg, opts) {
      try {
        const description = (opts && typeof opts === 'object' && opts.description) || '';
        const type = t === 'message' ? 'info' : (t === 'loading' ? 'info' : t);
        publishMessage(type, msg, description);
      } catch (_) {}
      return original(msg, opts);
    };
  }
})();

const LOG_KEY = 'clab_error_log';
const MAX_LOGS = 200;

// Global error logging (condiviso con utils/errorLogger.js)
const logError = (type, message, stack = '', extra = {}) => {
  const errorLog = {
    type,
    message: message || '(errore senza messaggio)',
    description: extra.description || '',
    url: extra.url || '',
    method: extra.method || '',
    status: extra.status || '',
    stack,
    timestamp: new Date().toISOString(),
  };

  try {
    const logs = JSON.parse(localStorage.getItem(LOG_KEY) || '[]');
    logs.unshift(errorLog);
    localStorage.setItem(LOG_KEY, JSON.stringify(logs.slice(0, MAX_LOGS)));
  } catch (_) {}

  console.error(`[${type}]`, message, stack, extra);
};

// Intercept window errors
window.addEventListener('error', (event) => {
  logError('ERROR', event.message, event.error?.stack || '');
});

// Intercept unhandled promise rejections
window.addEventListener('unhandledrejection', (event) => {
  logError('UNHANDLED_REJECTION', event.reason?.message || String(event.reason), event.reason?.stack || '');
});

// Intercept tutte le risposte axios in errore: ogni chiamata API fallita
// finisce nel log errori dell'admin, indipendentemente dal try/catch locale.
axios.interceptors.response.use(
  (res) => res,
  async (err) => {
    // Se la risposta è un Blob (download fallito con responseType:'blob'),
    // leggila come testo e prova a parsarla come JSON. Così err.response.data
    // diventa il payload {detail:'...'} normale e ogni catch a valle può
    // mostrare il detail leggibile invece del generico err.message.
    try {
      const rd = err?.response?.data;
      if (rd && typeof rd === 'object' && typeof rd.text === 'function' && (rd.size > 0 || rd.size === undefined)) {
        const txt = await rd.text();
        try {
          err.response.data = JSON.parse(txt);
        } catch {
          err.response.data = txt;
        }
        // Sovrascrive err.message con il detail per compat con catch
        // legacy che usano err.message.
        const d = err.response.data?.detail || (typeof err.response.data === 'string' ? err.response.data : null);
        if (d) err.message = d;
      }
    } catch (_) {}

    try {
      const cfg = err?.config || {};
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      const dataMsg = typeof err?.response?.data === 'string' ? err.response.data : '';
      const url = cfg.url || '';
      const method = (cfg.method || 'get').toUpperCase();
      // Skip 401 (rumore login/logout) e cancellazioni
      if (status !== 401 && err?.code !== 'ERR_CANCELED') {
        const baseMsg =
          detail ||
          err?.message ||
          dataMsg ||
          'Errore richiesta API';
        const title = status
          ? `API ${status} · ${method} ${url.replace(/^https?:\/\/[^/]+/, '')}`
          : `API errore · ${method} ${url.replace(/^https?:\/\/[^/]+/, '')}`;
        logError('API_ERROR', title, err?.stack || '', {
          description: baseMsg,
          url, method, status,
        });
      }
    } catch (_) {}
    return Promise.reject(err);
  }
);

// Service worker pass-through: registra un `fetch` handler per soddisfare il
// criterio di installabilità PWA di Chrome (su Android è obbligatorio perché
// compaia il prompt "Installa app"). Non fa caching → non interferisce mai col
// deploy via autopull. Vedi frontend/public/sw.js.
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {});
  });
}

// Su iPhone rientrare nell'app non ricarica la pagina: senza questo, dopo un
// deploy si continua a lavorare sul bundle vecchio. Vedi lib/versionCheck.js.
avviaControlloVersione();

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
